import { forwardRef, useEffect, useRef, useState } from 'react'

export type UITagProps = React.ComponentProps<'strong'> & {
  variant?: 'default' | 'big' | 'small'
  rounded?: 'default' | 'light' | 'lighter'
  theme?: 'default' | 'dark-blue' | 'light-blue' | 'mid-blue' | 'coral' | 'ochre'
  rotate?: boolean | 'forced'
}

const angles = ['rotate-2', 'rotate-3', '-rotate-2', '-rotate-3']

const UITag = forwardRef<HTMLDivElement, UITagProps>(
  ({ className, children, variant = 'default', rounded = 'default', theme = 'default', rotate = true }, ref) => {
    const inner = useRef<HTMLDivElement>(null)
    const [angle, setAngle] = useState<string>(null)

    useEffect(() => {
      if (inner.current) {
        if (rotate === 'forced' || (variant === 'default' && rotate === true)) {
          const words = inner.current.innerText.split(' ')
          if (rotate === 'forced' || words.length === 1) {
            const a = angles[Math.floor(Math.random() * angles.length)]
            setAngle(a)
          } else {
            setAngle(null)
          }
        } else {
          setAngle(null)
        }
      }
    }, [inner, variant, rotate])

    const variantClassName = (() => {
      switch (variant) {
        case 'big':
          return 'flex items-center justify-center h-[2em] px-[0.6em]'
        case 'small':
          return 'h-32 md:h-40 inline-flex items-center justify-center px-8'
        default:
          // return 'h-[1.33em] px-[0.3em]';
          return 'px-[0.5em]'
      }
    })()

    const themeBackgroundClassName = (() => {
      switch (theme) {
        case 'dark-blue':
          return 'bg-dark-blue'
        case 'light-blue':
          return 'bg-light-blue'
        case 'mid-blue':
          return 'bg-mid-blue'
        case 'coral':
          return 'bg-coral'
        case 'ochre':
          return 'bg-ochre'
        default:
          return 'bg-secondary'
      }
    })()
    const themeInnerClassName = (() => {
      switch (theme) {
        case 'light-blue':
          return 'text-primary'
        case 'dark-blue':
          return 'text-cream'
        case 'mid-blue':
          return 'text-cream'
        case 'coral':
          return 'text-primary'
        case 'ochre':
          return 'text-primary'
        default:
          return 'text-primary'
      }
    })()

    const roundedBackgroundClassName = (() => {
      switch (rounded) {
        case 'lighter':
          return 'rounded-[0.16em]'
        case 'light':
          return 'rounded-[0.6em]'
        default:
          return 'rounded-[0.8em]'
      }
    })()

    return (
      <strong
        ref={ref}
        className={`tag relative inline-flex items-center justify-center font-semibold ${
          angle ? angle : ''
        } ${variantClassName} ${className ? className : ''}`}
      >
        <span
          className={`tag__background absolute -top-4 left-0 -bottom-4 right-0 ${roundedBackgroundClassName} ${themeBackgroundClassName}`}
        />
        <span className='relative overflow-hidden'>
          <span ref={inner} className={`tag__inner relative inline-block ${themeInnerClassName}`}>
            {children}
          </span>
        </span>
      </strong>
    )
  }
)

export default UITag
